import { useEffect, useRef, useState } from 'react';
import { UseFormRegister, UseFormSetValue } from 'react-hook-form';

interface UseTurnstileProps {
    fieldName: string;
    setValue: UseFormSetValue<any>;
    register: UseFormRegister<any>;
    sitekey?: string;
    containerId?: string;
    onLoadingChange?: (isLoading: boolean) => void;
    onErrorChange?: (error: string | null) => void;
}

interface UseTurnstileReturn {
    widgetId: React.MutableRefObject<string | null>;
    isLoading: boolean;
    error: string | null;
    resetCaptcha: () => void;
    clearError: () => void;
}

export const useTurnstile = ({
    fieldName,
    setValue,
    register,
    sitekey = '0x4AAAAAAAixtkM12lRh0SU7',
    containerId = '#cf-turnstile',
    onLoadingChange,
    onErrorChange
}: UseTurnstileProps): UseTurnstileReturn => {
    const widgetId = useRef<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Notify parent component when loading state changes
    useEffect(() => {
        onLoadingChange?.(isLoading);
    }, [isLoading, onLoadingChange]);

    // Notify parent component when error state changes
    useEffect(() => {
        onErrorChange?.(error);
    }, [error, onErrorChange]);

    useEffect(() => {
        // Register the captcha field with react-hook-form
        register(fieldName, {
            required: 'Aguarde um momento e tente novamente!',
        });

        // Define the callback function for when Turnstile loads
        window.onloadTurnstileCallback = () => {
            if (window.turnstile && typeof window.turnstile.render === 'function' && !widgetId.current) {
                try {
                    widgetId.current = window.turnstile.render(containerId, {
                        sitekey,
                        callback: (token: string) => {
                            setValue(fieldName, token, { shouldValidate: true });
                            setIsLoading(false);
                            setError(null);
                        },
                        'error-callback': (errorCode?: string) => {
                            setIsLoading(false);
                            setError('Erro ao verificar captcha. Por favor, recarregue a página e tente novamente.');
                            // warn (não error): falhas do desafio (ex.: 600010, "bot detectado" — inclusive o
                            // navegador automatizado do PageSpeed) são esperadas e o widget tenta de novo sozinho
                            // (retry auto, a cada 8s). console.error derrubaria "Práticas recomendadas" no Lighthouse.
                            console.warn('Turnstile error:', errorCode);
                        },
                        'expired-callback': () => {
                            setIsLoading(true);
                            setError(null);
                        },
                        'timeout-callback': () => {
                            setIsLoading(false);
                            setError('A verificação expirou. Por favor, tente novamente.');
                        }
                    });
                } catch (err) {
                    setIsLoading(false);
                    setError('Erro ao carregar verificação. Por favor, recarregue a página.');
                    console.error('Turnstile render error:', err);
                }
            }
        };

        // If Turnstile is already loaded, call the callback immediately
        if (window.turnstile) {
            window.onloadTurnstileCallback();
        }

        // Cleanup function to remove the widget on unmount
        return () => {
            if (widgetId.current && window.turnstile) {
                window.turnstile.remove(widgetId.current);
                widgetId.current = null;
            }
        };
    }, [register, setValue, fieldName, sitekey, containerId]);

    // Function to reset the captcha widget
    const resetCaptcha = () => {
        if (widgetId.current && window.turnstile) {
            setIsLoading(true);
            setError(null);
            window.turnstile.reset(widgetId.current);
        }
    };

    // Function to clear error state
    const clearError = () => {
        setError(null);
    };

    return {
        widgetId,
        isLoading,
        error,
        resetCaptcha,
        clearError
    };
};
